import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import request from "../../utilities/requests";

// ── My Team Tab ────────────────────────────────────────────────────
const MyTeamTab = ({ myTeam }) => {
  if (!myTeam) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          You haven't been assigned to a team yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Card variant="outlined">
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {myTeam.team?.color && (
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: myTeam.team.color,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography variant="h6">{myTeam.team?.name}</Typography>
            {myTeam.division && (
              <Chip label={myTeam.division} size="small" variant="outlined" />
            )}
          </Box>
          {myTeam.roster?.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Roster
              </Typography>
              <List dense disablePadding>
                {myTeam.roster.map((player) => {
                  const firstName = player?.profile?.firstName || "";
                  const lastName = player?.profile?.lastName || "";
                  const name = `${firstName} ${lastName}`.trim();
                  if (!name) return null;
                  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                  return (
                    <ListItem key={player._id} disableGutters>
                      <ListItemAvatar>
                        <Avatar
                          src={player.profile?.imageUrl || undefined}
                          sx={{ width: 36, height: 36 }}
                        >
                          {initials}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={name} />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// ── Schedule Tab ───────────────────────────────────────────────────
const ScheduleTab = ({ games }) => {
  const formatDate = (dt) => dayjs(dt).format("MMM D");
  const formatTime = (dt) => dayjs(dt).format("h:mm A");

  const winnerOf = (game, side) => {
    if (!game.played) return false;
    if (game.team1Score === game.team2Score) return false;
    return side === 1
      ? game.team1Score > game.team2Score
      : game.team2Score > game.team1Score;
  };

  const upcomingGames = games
    .filter((g) => !g.played)
    .sort((a, b) => dayjs(a.scheduledAt).valueOf() - dayjs(b.scheduledAt).valueOf());
  const completedGames = games
    .filter((g) => g.played)
    .sort((a, b) => dayjs(b.scheduledAt).valueOf() - dayjs(a.scheduledAt).valueOf());

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      {/* Upcoming */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Upcoming
      </Typography>
      {upcomingGames.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No upcoming games scheduled.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {upcomingGames.map((game) => (
            <Card key={game._id} variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(game.scheduledAt)} · {formatTime(game.scheduledAt)}
                  {game.field ? ` · ${game.field}` : ""}
                </Typography>
                <Typography variant="body1">
                  {game.team1?.name || "TBD"} vs {game.team2?.name || "TBD"}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Completed */}
      {completedGames.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Results
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {completedGames.map((game) => (
              <Card key={game._id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(game.scheduledAt)}
                    {game.field ? ` · ${game.field}` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: winnerOf(game, 1) ? 700 : 400 }}>
                        {game.team1?.name || "TBD"}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: winnerOf(game, 2) ? 700 : 400 }}>
                        {game.team2?.name || "TBD"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {game.team1Score}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {game.team2Score}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// ── Standings Tab ──────────────────────────────────────────────────
const StandingsTab = ({ standings, myTeamId, publishedAt }) => {
  if (!standings || standings.length === 0) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Standings are not available yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      {standings.map((division) => (
        <Box key={division.division} sx={{ mb: 2 }}>
          {standings.length > 1 && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {division.division}
            </Typography>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, pl: 1, pr: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">W</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">L</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">T</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">+/-</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {division.teams.map((team) => (
                  <TableRow
                    key={team.team}
                    sx={
                      myTeamId && team.team?.toString() === myTeamId
                        ? { backgroundColor: "action.hover" }
                        : {}
                    }
                  >
                    <TableCell sx={{ pl: 1, pr: 0.5 }}>{team.position}</TableCell>
                    <TableCell>{team.teamName}</TableCell>
                    <TableCell align="center">{team.wins}</TableCell>
                    <TableCell align="center">{team.losses}</TableCell>
                    <TableCell align="center">{team.ties}</TableCell>
                    <TableCell align="center">
                      {team.pointDiff > 0 ? `+${team.pointDiff}` : team.pointDiff}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ))}
      {publishedAt && (
        <Typography variant="caption" color="text.secondary">
          Last updated {dayjs(publishedAt).format("MMM D, YYYY h:mm A")}
        </Typography>
      )}
    </Box>
  );
};

// ── Main Page ──────────────────────────────────────────────────────
const MyLeague = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [leagueEvent, setLeagueEvent] = useState(null);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (eventId) loadAll();
  }, [eventId]);

  const loadAll = async () => {
    setLoading(true);

    const leResult = await request(`leagueevent?eventId=${eventId}`);
    if (!leResult?.success) {
      setLoading(false);
      return;
    }
    const le = leResult.data;
    setLeagueEvent(le);

    const [gResult, sResult, tResult] = await Promise.all([
      request(`leaguegame?eventId=${eventId}`),
      request(`leagueevent/standings?leagueEventId=${le._id}`),
      request(`leagueevent/myteam?leagueEventId=${le._id}`),
    ]);

    if (gResult?.success) setGames(gResult.data);
    if (sResult?.success) setStandings(sResult.data);
    if (tResult?.success) setMyTeam(tResult.data);

    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!leagueEvent) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">League not found.</Typography>
      </Box>
    );
  }

  const event = leagueEvent.event;
  const myTeamId = myTeam?.team?._id?.toString();

  return (
    <Box sx={{ pb: 10, maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 2, pb: 1 }}>
        <IconButton onClick={() => navigate("/")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{event?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const parts = [];
              if (event?.start || event?.end) {
                const s = event.start ? dayjs(event.start).format("MMM D") : "";
                const e = event.end ? dayjs(event.end).format("MMM D, YYYY") : "";
                parts.push(s && e ? `${s} – ${e}` : s || e);
              }
              if (leagueEvent.sportType) parts.push(leagueEvent.sportType);
              if (event?.venueName) parts.push(event.venueName);
              return parts.join(" · ");
            })()}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="My Team" />
        <Tab label="Schedule" />
        <Tab label="Standings" />
      </Tabs>

      {tab === 0 && <MyTeamTab myTeam={myTeam} />}
      {tab === 1 && <ScheduleTab games={games} />}
      {tab === 2 && (
        <StandingsTab
          standings={standings}
          myTeamId={myTeamId}
          publishedAt={leagueEvent.standingsPublishedAt}
        />
      )}
    </Box>
  );
};

export default MyLeague;
